import React, { useMemo } from 'react';
import {
  isEditableCustomEvaluator,
  maximumHoldingState,
  setMaximumHoldingEnabled,
  setMaximumHoldingSessions,
  validateMaximumHolding,
} from '../customStrategyTimeExit';

const CustomStrategyEditor = ({
  strategyName,
  configuration,
  onStrategyNameChange,
  onConfigurationChange,
  parentEvaluatorVersion = null,
  idPrefix = 'custom-strategy',
  advancedOnly = false,
  semanticIntent = null,
  onSemanticIntentChange = null,
}) => {
  const parsed = useMemo(() => {
    try {
      return JSON.parse(configuration || 'null');
    } catch {
      return null;
    }
  }, [configuration]);

  const update = (change) => {
    if (!parsed) return;
    const next = structuredClone(parsed);
    change(next);
    onConfigurationChange(JSON.stringify(next, null, 2));
  };
  const updateIntent = (change, fallback) => {
    if (semanticIntent && onSemanticIntentChange) {
      const next = structuredClone(semanticIntent);
      change(next);
      onSemanticIntentChange(next);
      return;
    }
    fallback();
  };
  const indicator = (id) => parsed?.indicators?.find((item) => item.id === id);
  const rule = (id) => {
    const visit = (candidate) => {
      if (!candidate) return null;
      if (candidate.id === id) return candidate;
      return (candidate.children || []).map(visit).find(Boolean) || visit(candidate.child);
    };
    return visit(parsed?.entry_rule) || visit(parsed?.exit_policy?.technical_exit);
  };
  const setIndicatorWindow = (id, value) => update((next) => {
    const item = next.indicators.find((candidate) => candidate.id === id);
    if (item) item.window = Number(value);
  });
  const setRuleConstant = (id, value) => update((next) => {
    const visit = (candidate) => {
      if (!candidate) return null;
      if (candidate.id === id) return candidate;
      return (candidate.children || []).map(visit).find(Boolean) || visit(candidate.child);
    };
    const item = visit(next.entry_rule) || visit(next.exit_policy.technical_exit);
    if (item?.right?.kind === 'constant') item.right.value = String(value);
  });
  const holding = maximumHoldingState(parsed);
  const holdingError = validateMaximumHolding(holding);
  const canEditHolding = Boolean(
    parsed && (
      isEditableCustomEvaluator(parsed.evaluator_version)
      || (parentEvaluatorVersion && isEditableCustomEvaluator(parentEvaluatorVersion))
    ),
  );
  const plainIntent = parsed?.evaluator_version === 'custom-daily-rules-v3';
  const entryConsecutive = parsed?.entry_rule?.op === 'consecutive' ? parsed.entry_rule : null;
  const setExitTrigger = (kind, enabled) => update((next) => {
    next.exit_policy[kind] = enabled
      ? { enabled: true, percentage: kind === 'take_profit' ? '5' : '2', reference: 'entry_fill_price', trigger_basis: 'completed_daily_close' }
      : { enabled: false };
  });

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
      <p className="font-bold text-blue-950 dark:text-blue-100">
        Create your own reusable strategy. Existing Pinngro strategies are never changed.
      </p>
      <label className="mt-4 block text-sm font-bold" htmlFor={`${idPrefix}-name`}>Strategy name</label>
      <input
        id={`${idPrefix}-name`}
        value={strategyName}
        onChange={(event) => onStrategyNameChange(event.target.value)}
        minLength={3}
        maxLength={100}
        required
        placeholder="My conservative pullback"
        className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
      />
      {parsed && plainIntent && !advancedOnly && (
        <div className="mt-4 grid gap-4 sm:grid-cols-2" aria-label="Guided plain-English strategy rules">
          <fieldset className="rounded-xl border border-blue-200 p-4 dark:border-blue-800">
            <legend className="px-1 text-sm font-black">When to buy</legend>
            {entryConsecutive ? <label className="mt-2 block text-sm font-bold" htmlFor={`${idPrefix}-falling-sessions`}>Consecutive completed sessions
              <input id={`${idPrefix}-falling-sessions`} type="number" min="1" max="20" value={entryConsecutive.count} onChange={(event) => { const count = Number(event.target.value); updateIntent((next) => { next.entry.conditions[0].integer_parameter = count; }, () => update((next) => { next.entry_rule.count = count; next.required_lookback = Math.max(3, count + 1); })); }} className="mt-2 min-h-11 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" />
            </label> : <p className="mt-2 text-sm">Review the supported conditions in Advanced technical details.</p>}
            <p className="mt-2 text-xs text-slate-500">Each completed daily close is compared with the previous completed session.</p>
          </fieldset>
          <fieldset className="rounded-xl border border-blue-200 p-4 dark:border-blue-800">
            <legend className="px-1 text-sm font-black">When to sell</legend>
            {['take_profit', 'stop_loss'].map((kind) => {
              const trigger = parsed.exit_policy?.[kind] || { enabled: false };
              const label = kind === 'take_profit' ? 'Take profit' : 'Strategy stop-loss';
              const intentKey = kind === 'take_profit' ? 'take_profit_percentage' : 'stop_loss_percentage';
              return <div key={kind} className="mt-3"><label className="flex min-h-11 items-center gap-3 text-sm font-bold"><input type="checkbox" checked={Boolean(trigger.enabled)} onChange={(event) => updateIntent((next) => { next.exit[intentKey] = event.target.checked ? (kind === 'take_profit' ? '5' : '2') : null; }, () => setExitTrigger(kind, event.target.checked))} className="h-5 w-5" />{label}</label>{trigger.enabled && <label className="mt-2 block text-xs font-bold">{label} percentage<input type="number" min="0.01" max="100" step="0.01" value={trigger.percentage} onChange={(event) => updateIntent((next) => { next.exit[intentKey] = String(event.target.value); }, () => update((next) => { next.exit_policy[kind].percentage = String(event.target.value); }))} className="mt-1 min-h-11 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" /></label>}</div>;
            })}
            <p className="mt-3 text-xs text-slate-500">Other automatic sell rules: {parsed.exit_policy?.technical_exit?.enabled === false ? 'None' : 'Enabled'}. Pinngro runtime safety remains separate.</p>
          </fieldset>
          <div className="rounded-xl border border-blue-200 p-4 text-sm dark:border-blue-800"><strong>Re-entry</strong><p className="mt-1">The entry rule must happen again after a position closes.</p></div>
          <div className="rounded-xl border border-blue-200 p-4 text-sm dark:border-blue-800"><strong>Timing</strong><p className="mt-1">Rules use completed daily market sessions.</p></div>
          {canEditHolding && (
            <fieldset className="rounded-xl border border-blue-200 p-4 dark:border-blue-800 sm:col-span-2">
              <legend className="px-1 text-sm font-black">Maximum holding period</legend>
              <label className="mt-2 flex min-h-11 items-center gap-3 text-sm font-bold" htmlFor={`${idPrefix}-plain-maximum-holding-enabled`}>
                <input
                  id={`${idPrefix}-plain-maximum-holding-enabled`}
                  type="checkbox"
                  checked={holding.enabled}
                  onChange={(event) => updateIntent((next) => { next.exit.maximum_holding_sessions = event.target.checked ? 20 : null; }, () => onConfigurationChange(JSON.stringify(setMaximumHoldingEnabled(parsed, { enabled: event.target.checked, parentEvaluator: parsed.evaluator_version }), null, 2)))}
                  className="h-5 w-5"
                />
                Close after a maximum number of completed sessions
              </label>
              {holding.enabled && <label className="mt-2 block text-xs font-bold" htmlFor={`${idPrefix}-plain-maximum-holding-sessions`}>Completed sessions
                <input
                  id={`${idPrefix}-plain-maximum-holding-sessions`}
                  type="number"
                  min="1"
                  max="250"
                  value={holding.sessions}
                  onChange={(event) => updateIntent((next) => { next.exit.maximum_holding_sessions = Number(event.target.value); }, () => onConfigurationChange(JSON.stringify(setMaximumHoldingSessions(parsed, event.target.value), null, 2)))}
                  className="mt-1 min-h-11 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900"
                />
              </label>}
              <p className="mt-2 text-xs text-slate-500">This is a strategy exit rule. Platform and runtime safety remain separate.</p>
            </fieldset>
          )}
        </div>
      )}
      {parsed && !plainIntent && !advancedOnly ? (
        <div className="mt-4 grid gap-4 sm:grid-cols-2" aria-label="Custom strategy rules">
          <fieldset className="rounded-xl border border-blue-200 p-3 dark:border-blue-800">
            <legend className="px-1 text-sm font-black">Entry rules</legend>
            <label className="mt-2 block text-xs font-bold" htmlFor={`${idPrefix}-rsi-entry`}>RSI must be at or below</label>
            <input id={`${idPrefix}-rsi-entry`} type="number" min="0" max="100" value={rule('entry.rsi')?.right?.value || ''} onChange={(event) => setRuleConstant('entry.rsi', event.target.value)} className="mt-1 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" />
            <p className="mt-3 text-xs text-slate-500">MACD must be positive and price must be above SMA. Advanced technical details support the full existing rule contract.</p>
          </fieldset>
          <fieldset className="rounded-xl border border-blue-200 p-3 dark:border-blue-800">
            <legend className="px-1 text-sm font-black">Exit rules</legend>
            <label className="mt-2 block text-xs font-bold" htmlFor={`${idPrefix}-rsi-exit`}>RSI exit at or above</label>
            <input id={`${idPrefix}-rsi-exit`} type="number" min="0" max="100" value={rule('exit.rsi')?.right?.value || ''} onChange={(event) => setRuleConstant('exit.rsi', event.target.value)} className="mt-1 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" />
            <div className="mt-3 grid grid-cols-2 gap-2">
              {['stop_loss', 'take_profit'].map((kind) => (
                <label key={kind} className="text-xs font-bold">{kind === 'stop_loss' ? 'Stop-loss %' : 'Take-profit %'}
                  <input type="number" min="0.01" step="0.01" value={parsed.exit_policy?.[kind]?.percentage || ''} onChange={(event) => update((next) => { next.exit_policy[kind].percentage = String(event.target.value); })} className="mt-1 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" />
                </label>
              ))}
            </div>
            {canEditHolding && (
              <div className="mt-4 border-t border-blue-200 pt-4 dark:border-blue-800">
                <p className="text-xs font-black uppercase tracking-wide text-blue-700 dark:text-blue-300">Maximum holding period</p>
                <p className="mt-1 text-xs text-slate-500">Current setting: {holding.enabled ? `${holding.sessions} completed market sessions` : 'None'}</p>
                <label className="mt-3 flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border border-slate-300 bg-white p-3 text-xs dark:border-slate-700 dark:bg-slate-900" htmlFor={`${idPrefix}-maximum-holding-enabled`}>
                  <input
                    id={`${idPrefix}-maximum-holding-enabled`}
                    type="checkbox"
                    checked={holding.enabled}
                    onChange={(event) => onConfigurationChange(JSON.stringify(setMaximumHoldingEnabled(parsed, { enabled: event.target.checked, parentEvaluator: parentEvaluatorVersion }), null, 2))}
                    aria-describedby={`${idPrefix}-maximum-holding-help`}
                    className="mt-0.5 h-5 w-5 shrink-0"
                  />
                  <span><strong>Close a position after a maximum number of trading sessions</strong></span>
                </label>
                {holding.enabled && (
                  <>
                    <label className="mt-3 block text-xs font-bold" htmlFor={`${idPrefix}-maximum-holding-sessions`}>Maximum holding sessions</label>
                    <input
                      id={`${idPrefix}-maximum-holding-sessions`}
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max="250"
                      step="1"
                      value={holding.sessions}
                      onChange={(event) => onConfigurationChange(JSON.stringify(setMaximumHoldingSessions(parsed, event.target.value), null, 2))}
                      aria-describedby={`${idPrefix}-maximum-holding-help${holdingError ? ` ${idPrefix}-maximum-holding-error` : ''}`}
                      aria-invalid={Boolean(holdingError)}
                      className="mt-1 min-h-11 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900"
                    />
                  </>
                )}
                <p id={`${idPrefix}-maximum-holding-help`} className="mt-3 text-xs leading-5 text-slate-500">Choose 1–250 completed market sessions. The entry session counts as session 1 after the market closes. Saving creates a new version while the earlier version stays unchanged; Practice still requires approval for the resulting close proposal.</p>
                {holding.enabled && holdingError && <p id={`${idPrefix}-maximum-holding-error`} role="alert" className="mt-2 text-xs font-bold text-red-700 dark:text-red-300">{holdingError}</p>}
                <p className="mt-3 text-xs leading-5 text-slate-500">Pause stops new positions but preserves risk-reducing exit monitoring for an existing position.</p>
              </div>
            )}
          </fieldset>
          <fieldset className="rounded-xl border border-blue-200 p-3 dark:border-blue-800">
            <legend className="px-1 text-sm font-black">Indicators</legend>
            <div className="grid grid-cols-2 gap-2">
              {[['rsi14', 'RSI'], ['sma50', 'SMA'], ['atr14', 'ATR']].map(([id, label]) => (
                <label key={id} className="text-xs font-bold">{label} period
                  <input type="number" min="2" value={indicator(id)?.window || ''} onChange={(event) => setIndicatorWindow(id, event.target.value)} className="mt-1 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" />
                </label>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">MACD 12/26 is included. Its periods and crossover rules remain available under Advanced technical details.</p>
          </fieldset>
          <fieldset className="rounded-xl border border-blue-200 p-3 dark:border-blue-800">
            <legend className="px-1 text-sm font-black">Sentiment</legend>
            <label className="flex items-start gap-2 text-xs"><input type="checkbox" checked={Boolean(parsed.sentiment_policy?.enabled)} onChange={(event) => update((next) => { next.sentiment_policy = event.target.checked ? { enabled: true, source: 'newsapi-vader-v1', maximum_age_hours: 12, missing_data_policy: 'fail_closed', usage: 'entry_only', minimum_entry_score: '0', score_range: ['-1', '1'], replay_policy: 'pinned_evidence_required' } : { enabled: false }; })} /><span><strong>Require current sentiment evidence before entry</strong><br />If the required sentiment evidence is missing, the bot will not treat the entry rules as matched.</span></label>
            <details className="mt-3 text-xs text-slate-500"><summary className="cursor-pointer font-bold">Technical details</summary><p className="mt-2">Sentiment evidence is pinned to the evaluation. Reproducible Backtesting remains unavailable when that retained evidence does not exist.</p></details>
          </fieldset>
        </div>
      ) : !parsed ? <p className="mt-4 text-sm text-amber-800">The strategy definition is invalid. Correct the Advanced technical details before creating it.</p> : null}
      <details open={advancedOnly || undefined} className="mt-4 rounded-xl border border-blue-200 bg-white/70 p-3 dark:border-blue-800 dark:bg-slate-900/60">
        <summary className="cursor-pointer text-sm font-bold">Advanced technical details</summary>
        <p className="mt-2 text-xs text-slate-500">Compose supported rule trees and technical references. Executable code is never accepted.</p>
        <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
          <details><summary className="cursor-pointer font-bold">What does RSI mean?</summary><p className="mt-1">A momentum measure from 0 to 100. Pinngro can use it as one condition in a strategy.</p></details>
          <details><summary className="cursor-pointer font-bold">What does MACD mean?</summary><p className="mt-1">A measure Pinngro can use to check whether price momentum is strengthening or weakening.</p></details>
          <details><summary className="cursor-pointer font-bold">What does SMA mean?</summary><p className="mt-1">The average closing price over a chosen number of completed trading days.</p></details>
          <details><summary className="cursor-pointer font-bold">What does ATR mean?</summary><p className="mt-1">A measure of how much a stock has typically moved, not whether it is moving up or down.</p></details>
        </div>
        <textarea aria-label="Advanced strategy definition JSON" value={configuration} onChange={(event) => onConfigurationChange(event.target.value)} rows={16} spellCheck="false" className="mt-3 w-full rounded-xl border border-slate-300 bg-white p-3 font-mono text-xs dark:border-slate-700 dark:bg-slate-950" />
      </details>
    </div>
  );
};

export default CustomStrategyEditor;
